import React from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faVideo } from "@fortawesome/free-solid-svg-icons";
import {
  MessageLeftWrapper,
  Avatar,
  Message,
  Label,
  AttachmentWrapper,
  AttachmentIcon,
  AttachmentName,
} from "./styles/message-left-styles";

// Check if attachment is a video based on filename extension
const isVideoAttachment = (filename) => {
  if (!filename) return false;
  const videoExtensions = ['.mp4', '.mpeg', '.mov', '.avi', '.wmv', '.webm'];
  const lowerFilename = filename.toLowerCase();
  return videoExtensions.some(ext => lowerFilename.endsWith(ext));
};

// Check if attachment is an image based on filename extension
const isImageAttachment = (filename) => {
  if (!filename) return false;
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
  const lowerFilename = filename.toLowerCase();
  return imageExtensions.some(ext => lowerFilename.endsWith(ext));
};

const MessageLeft = (props) => {
  const {
    avatar,
    time,
    textContent,
    action,
    actionAt,
    author,
    attachmentLink,
    attachmentName,
    jobCurrencySymbol,
    finalSalary,
    rejected_reason,
  } = props;
  const label = action.replace(/_/g, " ");
  const fullName = `${author.first_name} ${author.last_name}`;

  const labelString = actionAt ? `${label}: ${actionAt}` : `${label}`;
  return (
    <MessageLeftWrapper>
      <Avatar
        src={avatar}
        data-tip={fullName}
        data-for="author-tooltip-left"
        alt="Avatar"
      />
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        aria-haspopup="true"
        id="author-tooltip-left"
      />
      <Message data-tip={time} data-for="time-left">
        <Label
          action={action}
          type={textContent !== "" ? "containsText" : null}
        >
          {finalSalary && action === "accepted_job"
            ? `${labelString}: ${jobCurrencySymbol}${finalSalary}`
            : labelString}
        </Label>
        {rejected_reason && rejected_reason["primary_reason"] && (
          <div style={{ paddingTop: "10px" }}>
            <p>
              <strong>Primary Reason:</strong>{" "}
              {rejected_reason["primary_reason"]?.name}
            </p>
            {rejected_reason["sub_reasons"]?.length > 0 && (
              <div>
                <p>
                  <strong>Sub Reasons:</strong>
                </p>
                <ul className="list-disc list-inside ml-4">
                  {rejected_reason["sub_reasons"].map((sub) => (
                    <li key={sub.id}>{sub.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {textContent}
        {attachmentLink && isVideoAttachment(attachmentName) && (
          <div style={{ marginTop: '10px' }}>
            <video
              controls
              style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }}
            >
              <source src={attachmentLink} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <AttachmentWrapper
              href={attachmentLink}
              target="_blank"
              title="Download Video"
              type={
                textContent !== "" || action !== "send_message"
                  ? "containsText"
                  : null
              }
              style={{ marginTop: '5px' }}
            >
              <AttachmentIcon>
                <FontAwesomeIcon icon={faVideo} />
              </AttachmentIcon>
              <AttachmentName>{attachmentName}</AttachmentName>
            </AttachmentWrapper>
          </div>
        )}
        {attachmentLink && isImageAttachment(attachmentName) && (
          <div style={{ marginTop: '10px' }}>
            <a href={attachmentLink} target="_blank" rel="noopener noreferrer" title="View full size">
              <img
                src={attachmentLink}
                alt={attachmentName || 'Attachment'}
                style={{
                  maxWidth: '250px',
                  maxHeight: '150px',
                  width: 'auto',
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            </a>
            <AttachmentWrapper
              href={attachmentLink}
              target="_blank"
              title="Download"
              type={
                textContent !== "" || action !== "send_message"
                  ? "containsText"
                  : null
              }
              style={{ marginTop: '5px' }}
            >
              <AttachmentIcon>
                <FontAwesomeIcon icon={faFileAlt} />
              </AttachmentIcon>
              <AttachmentName>{attachmentName}</AttachmentName>
            </AttachmentWrapper>
          </div>
        )}
        {attachmentLink && !isVideoAttachment(attachmentName) && !isImageAttachment(attachmentName) && (
          <AttachmentWrapper
            href={attachmentLink}
            target="_blank"
            title="Attachment"
            type={
              textContent !== "" || action !== "send_message"
                ? "containsText"
                : null
            }
          >
            <AttachmentIcon>
              <FontAwesomeIcon icon={faFileAlt} />
            </AttachmentIcon>
            <AttachmentName>{attachmentName}</AttachmentName>
          </AttachmentWrapper>
        )}
        <ReactTooltip
          place="top"
          type="dark"
          effect="solid"
          aria-haspopup="true"
          id="time-left"
        />
      </Message>
    </MessageLeftWrapper>
  );
};

MessageLeft.propTypes = {
  avatar: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  textContent: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  actionAt: PropTypes.instanceOf(Date),
  author: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }).isRequired,
  attachmentLink: PropTypes.string,
  attachmentName: PropTypes.string,
  jobCurrencySymbol: PropTypes.string.isRequired,
  finalSalary: PropTypes.number,
};

MessageLeft.defaultProps = {
  actionAt: null,
  attachmentLink: null,
  attachmentName: null,
  finalSalary: null,
};

export default MessageLeft;
